import { useState } from 'react'
import {
  AppShell, NavLink, Group, Text, UnstyledButton, Divider, Box,
  ActionIcon, useMantineColorScheme, useComputedColorScheme, ThemeIcon,
  Tooltip,
} from '@mantine/core'
import {
  IconDashboard,
  IconCalendarEvent,
  IconLeaf,
  IconUsers,
  IconUserCog,
  IconLogout,
  IconSun,
  IconMoon,
  IconLock,
  IconWorld,
  IconHome2,
  IconBuildingHospital,
  IconStethoscope,
  IconBed,
  IconPackage,
  IconNews,
  IconShieldCheck,
  IconBuildings,
  IconCategory,
  IconBriefcase,
  IconClipboardCheck,
  IconFileText,
} from '@tabler/icons-react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import type { AdminPermissionKey } from '@/constants'
import ChangePasswordModal from './ChangePasswordModal'

interface NavItem {
  label: string
  icon: typeof IconDashboard
  path: string
  permission: AdminPermissionKey
  section?: string
}

const navItems: NavItem[] = [
  { label: 'Хянах самбар', icon: IconDashboard, path: '/', permission: 'dashboard', section: 'ҮНДСЭН' },
  { label: 'Захиалгууд', icon: IconCalendarEvent, path: '/bookings', permission: 'bookings' },
  { label: 'Тасгууд', icon: IconBuildingHospital, path: '/departments', permission: 'services' },
  { label: 'Эмч нар', icon: IconStethoscope, path: '/doctors', permission: 'services' },
  { label: 'Үйлчилгээнүүд', icon: IconLeaf, path: '/services', permission: 'services' },
  { label: 'Үйлчилгээний ангилал', icon: IconCategory, path: '/svc-categories', permission: 'services' },
  { label: 'Багц эмчилгээ', icon: IconPackage, path: '/packages', permission: 'services' },
  { label: 'Өрөөнүүд', icon: IconBed, path: '/rooms', permission: 'services' },
  { label: 'Өрөөний захиалга', icon: IconBed, path: '/room-bookings', permission: 'bookings' },
  { label: 'Үйлчлүүлэгчид', icon: IconUsers, path: '/customers', permission: 'customers', section: 'ХЭРЭГЛЭГЧИД' },
  { label: 'Системийн хэрэглэгчид', icon: IconUserCog, path: '/system-users', permission: 'system_users', section: 'СИСТЕМ' },
  { label: 'Нүүр хуудас', icon: IconHome2, path: '/homepage', permission: 'website', section: 'ВЭБСАЙТ' },
  { label: 'Бидний тухай', icon: IconWorld, path: '/about', permission: 'website' },
  { label: 'Даатгал', icon: IconShieldCheck, path: '/insurance', permission: 'website' },
  { label: 'Холбоо барих', icon: IconWorld, path: '/contact', permission: 'website' },
  { label: 'Асуулт хариулт', icon: IconWorld, path: '/faqs', permission: 'website' },
  { label: 'Мэдээ', icon: IconNews, path: '/news', permission: 'website' },
  { label: 'Ажлын байр', icon: IconBriefcase, path: '/careers', permission: 'website' },
  { label: 'Судалгаа', icon: IconClipboardCheck, path: '/survey', permission: 'website' },
  { label: 'Илгээх бичиг', icon: IconFileText, path: '/referral-letter', permission: 'website' },
  { label: 'Салбарууд', icon: IconBuildings, path: '/branches', permission: 'website' },
]

export default function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, hasPermission } = useAuth()
  const { setColorScheme } = useMantineColorScheme()
  const computedColorScheme = useComputedColorScheme('light')
  const isDark = computedColorScheme === 'dark'
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  const visibleItems = navItems.filter((item) => hasPermission(item.permission))

  let lastSection = ''

  const toggleTheme = () => {
    setColorScheme(isDark ? 'light' : 'dark')
  }

  const displayName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Админ'
    : 'Админ'

  return (
    <AppShell navbar={{ width: 260, breakpoint: 'sm' }} padding="md">
      <AppShell.Navbar
        p="sm"
        style={{
          borderRight: `1px solid var(--mantine-color-${isDark ? 'dark-4' : 'gray-2'})`,
          backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-white)',
        }}
      >
        {/* Logo / Brand */}
        <Group mb="md" px="xs" justify="space-between">
          <Group gap="xs">
            <ThemeIcon size={36} radius="md" variant="gradient" gradient={{ from: 'green', to: 'teal', deg: 135 }}>
              <IconLeaf size={22} stroke={1.5} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={700} lh={1.2}>NatureMed</Text>
              <Text size="xs" c="dimmed" lh={1.2}>Админ самбар</Text>
            </div>
          </Group>
          <ActionIcon
            variant="subtle"
            onClick={toggleTheme}
            size="md"
            radius="md"
            color={isDark ? 'yellow' : 'blue'}
          >
            {isDark ? <IconSun size={16} /> : <IconMoon size={16} />}
          </ActionIcon>
        </Group>

        {/* Nav items */}
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          {visibleItems.map((item) => {
            const showSection = item.section && item.section !== lastSection
            if (item.section) lastSection = item.section

            return (
              <div key={item.path}>
                {showSection && (
                  <Text size="xs" fw={600} c="dimmed" mt="md" mb={4} px="xs" tt="uppercase" lts={1}>
                    {item.section}
                  </Text>
                )}
                <NavLink
                  label={item.label}
                  leftSection={<item.icon size={18} stroke={1.5} />}
                  active={
                    item.path === '/'
                      ? location.pathname === '/'
                      : location.pathname.startsWith(item.path)
                  }
                  onClick={() => navigate(item.path)}
                  mb={2}
                  styles={{
                    root: { borderRadius: 8, padding: '8px 10px' },
                    label: { fontSize: 13.5, fontWeight: 500 },
                  }}
                />
              </div>
            )
          })}
        </Box>

        <Divider mb="sm" />
        <Group px="xs" py="xs" justify="space-between">
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={500} truncate>{displayName}</Text>
            <Text size="xs" c="dimmed" truncate>{user?.email}</Text>
          </div>
          <Group gap={4}>
            <Tooltip label="Нууц үг солих" position="top">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                radius="md"
                onClick={() => setChangePasswordOpen(true)}
              >
                <IconLock size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Гарах" position="top">
              <UnstyledButton
                onClick={() => {
                  logout()
                  navigate('/login')
                }}
                style={{ color: 'var(--mantine-color-red-6)', display: 'flex', alignItems: 'center' }}
              >
                <IconLogout size={18} />
              </UnstyledButton>
            </Tooltip>
          </Group>
        </Group>
      </AppShell.Navbar>

      <AppShell.Main
        style={{
          backgroundColor: isDark ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-0)',
        }}
      >
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <Outlet />
        </div>
      </AppShell.Main>

      <ChangePasswordModal
        opened={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </AppShell>
  )
}
